import React, { useState } from 'react';
import { Col, convertToKRW, Row } from '../../lib/utils';
import { Body2Bold } from '../../lib/font';
import { MemberMembershipDataType, PaymentHistoryType } from '../../services/useMembershipQueryService';
import dayjs from 'dayjs';
import { DemiLightText, RegularText } from '../../lib/Text';
import SvgIcon from '../SvgIcon';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../Table/Table';
import { useMembershipTheme } from './useMembershipTheme';
import { CalendarPlus } from 'lucide-react';
import {
    isImmutableRefundRow,
    isLifecycleActionBlocked,
    MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE,
    MembershipLifecycle,
    REFUND_ROW_IMMUTABLE_TOOLTIP,
} from '../../lib/membershipLifecycle';

type Props = {
    membership: MemberMembershipDataType;
    /**
     * 종료(소프트 삭제)된 수강권의 생명주기 표식. 어떤 버튼을 잠글지는 **서버가 정한다**.
     * 프론트가 자체 규칙을 만들면 서버가 실제로 거부하는 목록과 갈린다(P2).
     */
    lifecycle: MembershipLifecycle;
    memberName?: string;
    coachName?: string;
    onClickUpdate: () => void;
    createAlert: any;
    onClickDelete: () => void;
    onClickRefund: () => void;
    onClickUpgrade: () => void;
    // 만료
    onClickExpire: () => void;
    // 재활성화
    onClickReactivation: () => void;
    // 미수금 결제
    onClickUnpaidPayment: () => void;
    // 클릭한 row
    onClickRow: (row: PaymentHistoryType) => void;
    // 분할 결제 버튼 클릭
    onClickPartialPayment: () => void;
    // 변경 이력 버튼 클릭
    onClickChangeLog: () => void;
    // 삭제 에러
    onDeleteError: () => void;
    // 수강권 상품변경
    onClickChangeProduct?: () => void;
    /**
     * 수강권 전환(그룹 전환 / 회차 공유) 선택 모달을 연다.
     * 두 전환은 이름만으로 구분되지 않아 버튼을 하나로 합치고 모달에서 나란히 비교시킨다.
     * 어느 쪽도 지금 고를 수 없으면 페이지가 이 콜백을 넘기지 않아 버튼 자체가 사라진다.
     */
    onClickConvert?: () => void;
    /** 상단 정보 카드 바로 아래에 붙는 공유 수강권 현황. 공유가 없으면 비어 있다. */
    sharedSlot?: React.ReactNode;
    /** 정보 카드 바로 아래 일시정지 구역(정지 정보·기능이 없으면 비어 있다) */
    pauseSlot?: React.ReactNode;
    // 그룹 수강권으로 수업 일괄 등록
    onClickRegisterGroupSessions?: () => void;
    // PT 수강권으로 1:1 수업 등록
    onClickRegisterPtSchedule?: () => void;
    isReadOnly?: boolean;
};
const columnHelper = createColumnHelper<PaymentHistoryType>();

const MemberMembershipManageComponents = ({
    membership,
    lifecycle,
    memberName,
    coachName,
    onClickUpdate,
    createAlert,
    onClickDelete,
    onClickRefund,
    onClickUpgrade,
    onClickExpire,
    onClickReactivation,
    onClickUnpaidPayment,
    onClickRow,
    onClickPartialPayment,
    onClickChangeLog,
    onDeleteError,
    onClickChangeProduct,
    onClickConvert,
    sharedSlot,
    pauseSlot,
    onClickRegisterGroupSessions,
    onClickRegisterPtSchedule,
    isReadOnly = false,
}: Props) => {
    // 호버 상태
    const [isHover, setIsHover] = useState(false);
    // 호버 인덱스
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    /**
     * 종료된 수강권에서 무엇을 잠글지는 **전부 서버 응답에서 나온다**(P2·P17).
     * 모바일 관리자 웹(M1)과 같은 해석을 쓴다 — 한쪽만 다르면 같은 수강권이 두 화면에서
     * 다르게 보인다.
     *
     * 두 갈래다.
     *   ① `lifecycleBlockedActions` 에 이름이 있는 것 — 서버가 409 로 실제 거부한다.
     *      업그레이드(L2·UPGRADE) / 미수금 결제(B16-lite·SPLIT_PAYMENT) / 상품 이동(L4·TEMPLATE_RELOCATE).
     *   ② `deletedContext` 로 알 수 있는 것 — 수정·삭제·상태 변경 계열. 서버가 상세 GET 만
     *      열었고 update/delete 는 여전히 소프트 삭제를 조회에서 걸러 **404** 로 떨어진다.
     *      목록에 이름이 없다고 열어 두면 누를 때마다 "오류 코드 404" 알럿만 뜬다.
     *
     * 어느 쪽도 프론트가 추측한 규칙이 아니다. 목록에도 없고 종료도 아니면 손대지 않는다.
     */
    const isLifecycleEnded = lifecycle.isEnded;
    const isUpgradeBlocked = isLifecycleActionBlocked(lifecycle, 'UPGRADE');
    const isSplitPaymentBlocked = isLifecycleActionBlocked(lifecycle, 'SPLIT_PAYMENT');
    const isTemplateRelocateBlocked =
        isLifecycleActionBlocked(lifecycle, 'TEMPLATE_RELOCATE') || isLifecycleEnded;
    /** 막힌 액션을 눌렀을 때의 공통 안내. 서버 메시지와 같은 문구를 쓴다(MB M1과 동일). */
    const showLifecycleEndedBlocked = () =>
        createAlert('종료된 수강권', MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE, true);
    const [isRegisterButtonHover, setIsRegisterButtonHover] = useState(false);
    const [isRegisterButtonActive, setIsRegisterButtonActive] = useState(false);
    // 판매상태, 결제일시 , 결제금액, 취소액, 총회차
    const columns = [
        columnHelper.accessor('saleStatus', {
            id: 'saleStatus',
            header: '판매상태',
            cell: (info) => {
                const text =
                    info.getValue() === 'COMPLETED'
                        ? '완료'
                        : info.getValue() === 'UPGRADED'
                        ? '업그레이드'
                        : info.getValue() === 'PARTIAL_REFUND'
                        ? '부분 환불'
                        : info.getValue() === 'PARTIAL_PAYMENT'
                        ? '분할 결제'
                        : '전체 환불';
                const textColor =
                    info.getValue() === 'COMPLETED'
                        ? '#3D4244'
                        : info.getValue() === 'UPGRADED'
                        ? '#9536D4'
                        : info.getValue() === 'PARTIAL_REFUND'
                        ? '#FF7220'
                        : info.getValue() === 'PARTIAL_PAYMENT'
                        ? '#15BA72'
                        : '#FF2020';

                /**
                 * 환불행은 수정 진입 자체를 막는다(P14 / U8).
                 *
                 * 신규 원매출 환불 API 가 만든 행은 회차를 "현재 수강권"에서 깎고 원판매
                 * totalPrice 는 보존한다. 구 결제행 수정·삭제 API 로 다루면 회차가 두 번
                 * 복원되고 원장과 어긋나므로 서버가 409 REFUND_ROW_IMMUTABLE 로 막는다.
                 *
                 * 판정은 **행의 refundOperationId** 로 한다. `saleStatus` 로 넓히면 구 PATCH
                 * 환불이 만든 레거시 환불행까지 잠겨 서버가 허용하는 편집을 화면에서만 없앤다.
                 * `lifecycleBlockedActions` 의 REFUND_ROW_EDIT 는 "이 화면에 그런 행이 있다"는
                 * 보조 신호일 뿐, 종료 수강권의 모든 결제행을 잠그라는 뜻이 아니다 —
                 * 그러면 이 패키지의 목적(매출 정정 진입점 복구)이 무너진다.
                 */
                const isRefundRowLocked = isImmutableRefundRow(info.row.original);
                // 호버
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return (
                    <Row
                        className="items-center"
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                    >
                        <RegularText fontSize={16} lineHeight={24} color={textColor} letterSpacing={-0.3}>
                            {text}
                        </RegularText>
                        {isRefundRowLocked && (
                            <span
                                title={REFUND_ROW_IMMUTABLE_TOOLTIP}
                                style={{
                                    marginLeft: 8,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    backgroundColor: '#F3F4F6',
                                    color: '#6B7280',
                                    border: '1px solid #E5E7EB',
                                    lineHeight: 1.4,
                                    whiteSpace: 'nowrap',
                                    cursor: 'help',
                                }}
                            >
                                수정 불가
                            </span>
                        )}
                        {!isReadOnly && !isRefundRowLocked && isHover && info.row.index === hoverIndex && (
                            <SvgIcon name="SvgBasicPencil" className=" absolute  right-[54px]" />
                        )}
                    </Row>
                );
            },
            size: 200,
            enableSorting: false,
        }),
        columnHelper.accessor('paymentDate', {
            id: 'paymentDate',
            header: '결제일시',
            cell: (info) => {
                return (
                    <RegularText fontSize={16} lineHeight={24} color={'#3D4244'}>
                        {dayjs(info.getValue()).format('YYYY-MM-DD')}
                    </RegularText>
                );
            },
            size: 200,
            enableSorting: false,
        }),
        columnHelper.accessor('paymentAmount', {
            id: 'paymentAmount',
            header: '결제금액',
            cell: (info) => {
                return (
                    <RegularText fontSize={16} lineHeight={24} color={'#3D4244'}>
                        {convertToKRW(info.getValue())}
                    </RegularText>
                );
            },
            size: 200,
            enableSorting: false,
        }),
        columnHelper.accessor('cancelAmount', {
            id: 'cancelAmount',
            header: '취소액',
            cell: (info) => {
                return (
                    <RegularText fontSize={16} lineHeight={24} color={'#3D4244'}>
                        {convertToKRW(info.getValue())}
                    </RegularText>
                );
            },
            size: 200,
            enableSorting: false,
        }),
        columnHelper.accessor('totalSession', {
            id: 'totalSession',
            header: '총회차',
            cell: (info) => {
                const textColor =
                    info.row.original.saleStatus === 'COMPLETED'
                        ? '#3D4244'
                        : info.row.original.saleStatus === 'UPGRADED'
                        ? '#9536D4'
                        : info.row.original.saleStatus === 'PARTIAL_REFUND'
                        ? '#FF7220'
                        : '#FF2020';

                const offsetText =
                    info.row.original.totalSessionOffset === 0
                        ? ''
                        : info.row.original.saleStatus === 'COMPLETED' || info.row.original.saleStatus === 'UPGRADED'
                        ? `(+${info.row.original.totalSessionOffset})`
                        : `(-${info.row.original.totalSessionOffset})`;

                return (
                    <>
                        <RegularText fontSize={16} lineHeight={24} color={'#3D4244'}>
                            {info.getValue()}
                        </RegularText>
                        <RegularText fontSize={16} lineHeight={24} color={textColor}>
                            {offsetText}
                        </RegularText>
                    </>
                );
            },
            size: 200,
            enableSorting: false,
        }),
    ];
    // 수강권 테마 — gradient/배지/정책 라벨을 한 곳에서 계산 (Phase 2 UX 통일화)
    const theme = useMembershipTheme(membership);
    const { isActive, headerBackground, expiryPolicyLabel, sourceLabel } = theme;
    const isGroup = (membership as any)?.source === 'group';
    // 종료된 수강권에는 schedules 가 오지 않는다. 보관 이력으로 같은 판정을 한다 —
    // 안 그러면 "일정이 하나도 없는데 일정이 있어 못 지운다"는 안내가 뜬다.
    const scheduleRows = membership?.schedules ?? membership?.archivedScheduleHistory ?? [];
    const isEmptySchedule = isGroup
        ? !scheduleRows.some((s: any) => s.bookingStatus !== 'CANCELLED')
        : scheduleRows.length === 0;

    // 담당강사 인계 시 결제 원장은 원판매 수강권에 남고 인계본에는 붙지 않는다.
    // 그래서 인계본 상세는 판매금액 0원 / 결제내역 없음으로 보인다.
    // 백엔드가 인계 계보를 이어 chain* 로 병기해 주면 "표시"에만 그 값을 쓴다.
    //
    // 주의: 표시값을 동작(수정·결제)에 넘기지 말 것.
    //   - 판매가 수정에 계보 판매가를 넣으면 차액이 원장에 가산되어 매출이 부풀려진다.
    //   - 미수금 결제를 계보 미수금으로 열면 인계본(판매가 0)에 결제행이 붙어 음수가 된다.
    // 그래서 아래 raw 값(unpaidPrice 등)은 그대로 두고 display* 만 따로 만든다.
    const chainPaymentHistory = (membership as any)?.chainPaymentHistory;
    const hasChainPayments = Array.isArray(chainPaymentHistory);
    const lineageIncomplete = (membership as any)?.chainLineageIncomplete === true;

    const paymentHistory = hasChainPayments ? chainPaymentHistory : membership?.paymentHistory;

    // 남은 일수
    const remainDays = dayjs(membership?.expirationDate).diff(dayjs(membership?.activationDate), 'day');

    const startDate = dayjs(membership?.activationDate).format('YYYY-MM-DD');
    const endDate = dayjs(membership?.expirationDate).format('YYYY-MM-DD');
    const isNewSalesText = membership?.isNewSales ? '신규' : '재등록';
    const isNewSalesBgColor = membership?.isNewSales ? '#7E7EF5' : '#64637B';
    // 판매 담당자 — 다양한 필드명 fallback (레거시 데이터 호환)
    const salesManagerName =
        membership?.salesManager?.userName ||
        (membership?.salesManager as any)?.name ||
        (membership?.salesManager as any)?.userId ||
        (typeof membership?.salesManager === 'string' ? membership?.salesManager : undefined);
    // 수업 시간
    const scheduleTime = membership?.period;
    // 회당 가격
    const pricePerSchedule = membership?.pricePerSchedule;
    // 시작 회차
    const startSession = membership?.startSessionValue;
    // 결제 금액 (raw — 수정 경로가 쓰는 값)
    const totalPrice = membership?.totalPrice;
    // 표시용 판매금액 — 인계본이면 계보 원판매의 판매가
    const displayTotalPrice = hasChainPayments ? (membership as any)?.chainTotalPrice : totalPrice;
    // 현재 회차
    const currentSession = membership?.currentSession;
    // 총 회차
    const totalSession = membership?.totalSession;
    // 결제 일자
    const paymentDate = dayjs(membership?.initialPaymentDate).format('YYYY-MM-DD');
    // 메모
    const memo = membership?.memo;
    // 결제 금액
    const totalPaymentAmount = membership?.totalPaymentAmount;
    const displayTotalPaymentAmount = hasChainPayments
        ? (membership as any)?.chainTotalPaymentAmount
        : totalPaymentAmount;
    // 미수금액 (raw — "미수금 결제" 버튼 노출 판단은 반드시 이 값으로 한다)
    const unpaidPrice = membership?.totalUnpaidAmount;
    const displayUnpaidPrice = hasChainPayments
        ? (membership as any)?.chainTotalUnpaidAmount
        : unpaidPrice;
    const groupSummaryCards = isGroup
        ? [
              {
                  label: '예약 차감',
                  value: `${membership?.activeReservationCount ?? 0}건`,
                  helper: '예약 확정 즉시 1회 차감',
                  bgColor: '#EFF6FF',
                  borderColor: '#BFDBFE',
                  textColor: '#1D4ED8',
              },
              {
                  label: '출석 사용',
                  value: `${membership?.completedCount ?? 0}건`,
                  helper: '수업 종료 후 사용 처리',
                  bgColor: '#ECFDF5',
                  borderColor: '#A7F3D0',
                  textColor: '#047857',
              },
              {
                  label: '노쇼 차감',
                  value: `${membership?.noshowCount ?? 0}건`,
                  helper:
                      (membership?.noshowPenaltyCount ?? 0) > 0
                          ? `추가 패널티 ${membership?.noshowPenaltyCount ?? 0}회 포함`
                          : '노쇼 패널티 없거나 기본 1회 차감',
                  bgColor: '#FEF2F2',
                  borderColor: '#FECACA',
                  textColor: '#B91C1C',
              },
              {
                  label: '취소 복원',
                  value: `${membership?.restoredCancelCount ?? 0}건`,
                  helper: '정상 취소 또는 패널티 면제',
                  bgColor: '#F0FDF4',
                  borderColor: '#BBF7D0',
                  textColor: '#15803D',
              },
              {
                  label: '차감 유지 취소',
                  value: `${membership?.lateCancelledCount ?? 0}건`,
                  helper: '늦은 취소 등으로 회차 유지',
                  bgColor: '#FFF7ED',
                  borderColor: '#FED7AA',
                  textColor: '#C2410C',
              },
              {
                  label: '대기 예약',
                  value: `${membership?.waitlistedCount ?? 0}건`,
                  helper: '대기 상태는 회차 차감 없음',
                  bgColor: '#F8FAFC',
                  borderColor: '#CBD5E1',
                  textColor: '#475569',
              },
          ]
        : [];
    const remainSessionValue =
        membership?.remainSession ?? Math.max((totalSession ?? 0) - (currentSession ?? 0), 0);
    const isRegisterDisabled = !isActive || remainSessionValue <= 0;
    const getRegisterButtonStyle = (disabled = false): React.CSSProperties => ({
        height: 44,
        padding: '0 16px',
        borderRadius: 8,
        border: `1px solid ${
            disabled
                ? '#D1D5DB'
                : isRegisterButtonActive
                ? '#34D399'
                : isRegisterButtonHover
                ? '#6EE7B7'
                : '#A7F3D0'
        }`,
        backgroundColor: disabled
            ? '#F3F4F6'
            : isRegisterButtonActive
            ? '#A7F3D0'
            : isRegisterButtonHover
            ? '#D1FAE5'
            : '#ECFDF5',
        color: disabled ? '#9CA3AF' : '#047857',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        boxShadow: !disabled && isRegisterButtonHover ? '0 8px 18px rgba(5, 150, 105, 0.16)' : 'none',
        transform: !disabled && isRegisterButtonHover && !isRegisterButtonActive ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease',
    });

    return (
        <Col className="flex  h-full mb-[80px]  px-[55px] pb-[55px] pt-[25px] rounded-[12px] bg-white">
            <Row className="flex flex-row self-end mb-[18px]">
                <Row
                    className="bg-[#F2F4F6] mr-[12px] items-center w-[145px] justify-center py-[4px] rounded-[5px] self-end mt-[20px] border-[1px] border-solid border-[#E5E5E9] hover:bg-[#E2E7EF] cursor-pointer"
                    onClick={onClickChangeLog}
                >
                    <SvgIcon name="SvgLineHistory" size={16} fill="none" stroke="#5C626A" className="mr-[15px]" />
                    <DemiLightText fontSize={15} lineHeight={21} letterSpacing={-0.32} color="#3D4244">
                        변경 이력
                    </DemiLightText>
                </Row>
                {!isReadOnly && (() => {
                    // 이미 분할결제거나 미수금이 이미 발생한 경우 버튼 자체를 숨김
                    const alreadySplitOrUnpaid =
                        !!membership?.isSplitPayment || (membership?.totalUnpaidAmount ?? 0) > 0;
                    if (alreadySplitOrUnpaid) return null;
                    return (
                        <Row
                            className={`mr-[12px] items-center w-[163px] justify-center py-[4px] rounded-[5px] self-end mt-[20px] border-[1px] border-solid ${
                                isLifecycleEnded
                                    ? 'bg-[#F3F4F6] border-[#E5E7EB] cursor-not-allowed'
                                    : 'bg-[#F2F4F6] border-[#E5E5E9] hover:bg-[#E2E7EF] cursor-pointer'
                            }`}
                            title={isLifecycleEnded ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                            onClick={isLifecycleEnded ? showLifecycleEndedBlocked : onClickPartialPayment}
                        >
                            <SvgIcon
                                name="SvgParticlePay"
                                width={20}
                                height={16}
                                fill={isLifecycleEnded ? '#9CA3AF' : '#5C626A'}
                                className="mr-[15px]"
                            />
                            <DemiLightText
                                fontSize={15}
                                lineHeight={21}
                                letterSpacing={-0.32}
                                color={isLifecycleEnded ? '#9CA3AF' : '#3D4244'}
                            >
                                {isLifecycleEnded ? '종료된 수강권' : '분할 결제로 변경'}
                            </DemiLightText>
                        </Row>
                    );
                })()}
                {/* 종료된 수강권의 상품 이동은 서버가 스킵한다(L4/B17). 비활성 + 사유로 알린다. */}
                {!isReadOnly && onClickChangeProduct && (
                    <Row
                        className={`mr-[12px] items-center w-[163px] justify-center py-[4px] rounded-[5px] self-end mt-[20px] border-[1px] border-solid ${
                            isTemplateRelocateBlocked
                                ? 'bg-[#F3F4F6] border-[#E5E7EB] cursor-not-allowed'
                                : 'bg-[#F2F4F6] border-[#E5E5E9] hover:bg-[#E2E7EF] cursor-pointer'
                        }`}
                        title={isTemplateRelocateBlocked ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                        onClick={isTemplateRelocateBlocked ? showLifecycleEndedBlocked : onClickChangeProduct}
                    >
                        <SvgIcon
                            name="SvgNavTicket"
                            size={16}
                            fill={isTemplateRelocateBlocked ? '#9CA3AF' : '#5C626A'}
                            stroke={isTemplateRelocateBlocked ? '#9CA3AF' : '#5C626A'}
                            className="mr-[12px]"
                        />
                        <DemiLightText
                            fontSize={15}
                            lineHeight={21}
                            letterSpacing={-0.32}
                            color={isTemplateRelocateBlocked ? '#9CA3AF' : '#3D4244'}
                        >
                            {isTemplateRelocateBlocked ? '종료된 수강권' : '수강권 상품변경'}
                        </DemiLightText>
                    </Row>
                )}
                {!isReadOnly && <Row
                    className={`items-center w-[145px] justify-center py-[4px] rounded-[5px] self-end mt-[20px] border-[1px] border-solid ${
                        isLifecycleEnded
                            ? 'bg-[#F3F4F6] border-[#E5E7EB] cursor-not-allowed'
                            : 'bg-[#F2F4F6] border-[#E5E5E9] hover:bg-[#E2E7EF] cursor-pointer'
                    }`}
                    title={isLifecycleEnded ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                    onClick={isLifecycleEnded ? showLifecycleEndedBlocked : onClickUpdate}
                >
                    <SvgIcon
                        name="SvgPencil"
                        size={15}
                        fill={isLifecycleEnded ? '#9CA3AF' : '#5C626A'}
                        className="mr-[15px]"
                    />
                    <DemiLightText
                        fontSize={15}
                        lineHeight={21}
                        letterSpacing={-0.32}
                        color={isLifecycleEnded ? '#9CA3AF' : '#3D4244'}
                    >
                        {isLifecycleEnded ? '종료된 수강권' : '수강권 수정'}
                    </DemiLightText>
                </Row>}
            </Row>
            <Row className="flex flex-row justify-center  h-[150px]  mb-[56px] ">
                <Col
                    className="flex flex-col min-w-[275px] rounded-[12px] h-full   justify-center items-center mr-[17px]"
                    style={{
                        background: headerBackground,
                        alignItems: 'center',
                    }}
                >
                    <Col className="flex w-full h-full pt-[16px] pb-[21px]   items-center">
                        <Row className="self-start mx-[21px] mb-[28px] items-center gap-[8px]">
                            <Body2Bold className="text-LAVEL_0">{`${membership?.name ?? ''}`}</Body2Bold>
                            {/* 소스 배지 — PT/그룹 구분. 카드 헤더에만 노출. */}
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    backgroundColor: 'rgba(255,255,255,0.22)',
                                    color: '#FFFFFF',
                                    lineHeight: 1.4,
                                }}
                            >
                                {sourceLabel}
                            </span>
                        </Row>
                        <Row className=" items-center">
                            <Col className="bg-white bg-opacity-[34%] rounded-[18px] w-[60px] h-[25px] justify-center items-center mr-[7px] ">
                                <RegularText fontSize={11} lineHeight={15.93} color="white">
                                    이용 기간
                                </RegularText>
                            </Col>
                            <RegularText
                                fontSize={15}
                                lineHeight={21.72}
                                color="white"
                                className=" opacity-[0.6]"
                                letterSpacing={-0.3}
                            >
                                {`${startDate} ~ ${endDate}`}
                            </RegularText>
                        </Row>
                    </Col>
                </Col>
                <Col className="flex relative px-[27px] py-[18px]  h-full border border-solid border-[#E5E5E9] bg-white bg-opacity-[0.6] rounded-[12px] justify-between items-center">
                    <Row className="w-full justify-between ">
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                회원
                            </DemiLightText>
                            <Row className="w-[170px] ">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={23}
                                    letterSpacing={-0.3}
                                    color="#3D4244"
                                    className="mr-[5px]"
                                >
                                    {memberName}
                                </RegularText>
                                <Col
                                    className=" justify-center items-center rounded-[16px] w-[45px] h-[23px]"
                                    style={{ backgroundColor: isNewSalesBgColor }}
                                >
                                    <DemiLightText fontSize={12} lineHeight={24} color="#FFFFFF">
                                        {isNewSalesText}
                                    </DemiLightText>
                                </Col>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                {isGroup ? '유효 기간' : '수업시간'}
                            </DemiLightText>
                            <Row className="w-[170px] ">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={23}
                                    letterSpacing={-0.3}
                                    color="#3D4244"
                                    className="mr-[5px]"
                                >
                                    {isGroup
                                        ? (remainDays > 0 ? `${remainDays}일` : '-')
                                        : (scheduleTime ? scheduleTime + '분' : '없음')}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[70px]"
                            >
                                회당 가격
                            </DemiLightText>
                            <Row className=" w-[80px] justify-end ">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {pricePerSchedule ? convertToKRW(pricePerSchedule) : '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                    </Row>

                    <Row className="w-full justify-between ">
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                자동 만료 정책
                            </DemiLightText>
                            <Row className="w-[170px] items-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={23}
                                    letterSpacing={-0.3}
                                    color={expiryPolicyLabel ? '#4338CA' : '#3D4244'}
                                >
                                    {expiryPolicyLabel || '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                시작 회차
                            </DemiLightText>
                            <Row className="w-[170px]">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {startSession ? startSession + '회' : '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[70px]"
                            >
                                판매 금액
                            </DemiLightText>
                            <Row className=" w-[80px] justify-end ">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {displayTotalPrice ? convertToKRW(displayTotalPrice) : '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                    </Row>
                    <Row className="flex w-full justify-between ">
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                판매 담당자
                            </DemiLightText>
                            <Row className="w-[170px]">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {salesManagerName && salesManagerName.trim() ? salesManagerName : '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                현재/총 회차
                            </DemiLightText>
                            <Row className="w-[170px]">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {currentSession + '/' + totalSession + '회'}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[70px]"
                            >
                                결제 금액
                            </DemiLightText>
                            <Row className=" w-[80px] justify-end ">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {displayTotalPaymentAmount ? convertToKRW(displayTotalPaymentAmount) : '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                    </Row>
                    <Row className="w-full justify-between ">
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                발급일자
                            </DemiLightText>
                            <Row className="w-[170px]">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {dayjs(membership?.createdAt).format('YYYY-MM-DD')}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[120px]"
                            >
                                결제일자
                            </DemiLightText>
                            <Row className="w-[170px]">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {paymentDate}
                                </RegularText>
                            </Row>
                        </Row>
                        <Row>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.3}
                                color="#505967"
                                className="w-[70px]"
                            >
                                미수금액
                            </DemiLightText>
                            <Row className=" w-[80px] justify-end ">
                                <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="#3D4244">
                                    {displayUnpaidPrice ? convertToKRW(displayUnpaidPrice) : '없음'}
                                </RegularText>
                            </Row>
                        </Row>
                    </Row>
                </Col>
            </Row>
            {/*
              공유 현황은 "이 수강권을 누가 쓰는가"라는 정체성 정보다.
              삭제·환불 버튼 아래에 두면 위에서부터 읽는 사람은 개인 수강권으로 오해한다.
              정보 카드 바로 다음, 수업 등록보다 앞에 놓는다.
            */}
            {/* 일시정지는 "지금 이 수강권을 쓸 수 있는가"라 공유 현황보다 먼저 읽힌다. */}
            {pauseSlot}
            {sharedSlot}
            {isGroup && (
                <Col className="mb-[32px] rounded-[16px] border border-solid border-[#E5E7EB] bg-[#FCFDFE] px-[24px] py-[20px]">
                    <Row className="mb-[16px] items-start justify-between gap-[16px]">
                        <Col className="flex-1">
                            <DemiLightText
                                fontSize={18}
                                lineHeight={24}
                                letterSpacing={-0.3}
                                color="#111827"
                                className="mb-[6px]"
                            >
                                그룹수업 회차 반영 요약
                            </DemiLightText>
                            <RegularText fontSize={14} lineHeight={21} letterSpacing={-0.2} color="#6B7280">
                                예약 확정 시 바로 1회 차감되고, 취소 시 복원 여부와 노쇼 패널티는 아래 회차
                                영향 기록에 그대로 남습니다.
                            </RegularText>
                        </Col>
                        <Row className="items-start gap-[10px]">
                            {!isReadOnly && isActive && remainSessionValue > 0 && onClickRegisterGroupSessions && (
                                <button
                                    type="button"
                                    onClick={onClickRegisterGroupSessions}
                                    onMouseEnter={() => setIsRegisterButtonHover(true)}
                                    onMouseLeave={() => {
                                        setIsRegisterButtonHover(false);
                                        setIsRegisterButtonActive(false);
                                    }}
                                    onMouseDown={() => setIsRegisterButtonActive(true)}
                                    onMouseUp={() => setIsRegisterButtonActive(false)}
                                    style={getRegisterButtonStyle(false)}
                                >
                                    <CalendarPlus size={17} />
                                    수업 등록
                                </button>
                            )}
                            <Col className="items-end rounded-[12px] bg-[#F8FAFC] px-[16px] py-[12px]">
                                <RegularText fontSize={12} lineHeight={18} letterSpacing={-0.2} color="#6B7280">
                                    남은 회차
                                </RegularText>
                                <DemiLightText fontSize={22} lineHeight={28} letterSpacing={-0.4} color="#111827">
                                    {remainSessionValue}회
                                </DemiLightText>
                            </Col>
                        </Row>
                    </Row>
                    <Row className="flex-wrap gap-[12px]">
                        {groupSummaryCards.map((item) => (
                            <Col
                                key={item.label}
                                className="min-h-[92px] min-w-[160px] flex-1 rounded-[14px] border border-solid px-[16px] py-[14px]"
                                style={{
                                    backgroundColor: item.bgColor,
                                    borderColor: item.borderColor,
                                }}
                            >
                                <RegularText
                                    fontSize={12}
                                    lineHeight={18}
                                    letterSpacing={-0.2}
                                    color="#6B7280"
                                    className="mb-[8px]"
                                >
                                    {item.label}
                                </RegularText>
                                <DemiLightText
                                    fontSize={22}
                                    lineHeight={28}
                                    letterSpacing={-0.4}
                                    color={item.textColor}
                                    className="mb-[6px]"
                                >
                                    {item.value}
                                </DemiLightText>
                                <RegularText fontSize={12} lineHeight={18} letterSpacing={-0.2} color="#4B5563">
                                    {item.helper}
                                </RegularText>
                            </Col>
                        ))}
                    </Row>
                </Col>
            )}
            {!isGroup && (
                <Col className="mb-[32px] rounded-[16px] border border-solid border-[#E5E7EB] bg-[#FCFDFE] px-[24px] py-[20px]">
                    <Row className="items-center justify-between gap-[16px]">
                        <Col>
                            <DemiLightText
                                fontSize={18}
                                lineHeight={24}
                                letterSpacing={-0.3}
                                color="#111827"
                                className="mb-[6px]"
                            >
                                PT 수업 등록
                            </DemiLightText>
                            <RegularText fontSize={14} lineHeight={21} letterSpacing={-0.2} color="#6B7280">
                                {isRegisterDisabled
                                    ? isActive
                                        ? '남은 회차 없음'
                                        : '비활성 수강권'
                                    : `${memberName || '회원'} · ${coachName || '담당 강사 미지정'}`}
                            </RegularText>
                        </Col>
                        <Row className="items-center gap-[10px]">
                            <Col className="items-end rounded-[12px] bg-[#F8FAFC] px-[16px] py-[12px]">
                                <RegularText fontSize={12} lineHeight={18} letterSpacing={-0.2} color="#6B7280">
                                    남은 회차
                                </RegularText>
                                <DemiLightText fontSize={22} lineHeight={28} letterSpacing={-0.4} color="#111827">
                                    {remainSessionValue}회
                                </DemiLightText>
                            </Col>
                            {!isReadOnly && <button
                                type="button"
                                disabled={isRegisterDisabled || !onClickRegisterPtSchedule}
                                onClick={() => {
                                    if (isRegisterDisabled || !onClickRegisterPtSchedule) return;
                                    onClickRegisterPtSchedule();
                                }}
                                onMouseEnter={() => {
                                    if (!isRegisterDisabled) setIsRegisterButtonHover(true);
                                }}
                                onMouseLeave={() => {
                                    setIsRegisterButtonHover(false);
                                    setIsRegisterButtonActive(false);
                                }}
                                onMouseDown={() => {
                                    if (!isRegisterDisabled) setIsRegisterButtonActive(true);
                                }}
                                onMouseUp={() => setIsRegisterButtonActive(false)}
                                style={getRegisterButtonStyle(isRegisterDisabled || !onClickRegisterPtSchedule)}
                            >
                                <CalendarPlus size={17} />
                                수업 등록
                            </button>}
                        </Row>
                    </Row>
                </Col>
            )}
            {lineageIncomplete && (
                <Row className="mb-[12px] px-[14px] py-[10px] bg-[#FFF7ED] border border-solid border-[#FDBA74] rounded-[8px]">
                    <RegularText fontSize={13} lineHeight={19} letterSpacing={-0.2} color="#C2410C">
                        인계 연결이 끊겨 있어 원판매 결제 내역을 불러오지 못했습니다. 이 수강권에 직접
                        기록된 내역만 표시됩니다.
                    </RegularText>
                </Row>
            )}
            {paymentHistory && (
                <Table
                    data={paymentHistory}
                    columns={columns}
                    className="mb-[41px]"
                    onRowClick={(row: PaymentHistoryType) => {
                        // 인계 여부와 무관하게 모든 결제행을 똑같이 수정·삭제한다.
                        // 회원과 수강권 입장에서는 담당강사만 바뀐 하나의 수강권이고,
                        // "원판매 수강권"은 내부 구현 사정이지 운영자가 알 개념이 아니다.
                        // 서버가 신규 환불행을 알아보고 회차를 실제 적용 대상 수강권에
                        // 되돌리므로 여기서 구분할 필요가 없다.
                        //
                        // 예외는 신규 환불 API 가 만든 환불행 하나다(P14). 서버가 409 로
                        // 막으므로 모달을 열어 두면 저장이 무반응으로만 보인다.
                        if (isImmutableRefundRow(row)) {
                            createAlert('수정할 수 없는 내역', REFUND_ROW_IMMUTABLE_TOOLTIP, true);
                            return;
                        }
                        if (!isReadOnly) onClickRow(row);
                    }}
                    onRowHoverMouseEnter={(index) => {
                        setIsHover(true);
                        setHoverIndex(index);
                    }}
                    onRowHoverMouseLeave={() => {
                        setIsHover(false);
                        setHoverIndex(null);
                    }}
                    hoverColor="#F1F3F6"
                    rowHeight={48}
                />
            )}
            <Col className="w-full h-[172px] border border-solid border-[#E5E5E9] rounded-[23px] py-[21px] px-[25px] mb-[51px]">
                <DemiLightText
                    fontSize={18}
                    lineHeight={23}
                    letterSpacing={-0.3}
                    color="#505967"
                    className=" w-full break-all h-full line-clamp-4 text-ellipsis opacity-[60%]"
                >
                    {memo ? memo : '메모가 작성되지 않은 수강권입니다'}
                </DemiLightText>
            </Col>
            {/*
              탈퇴 회원이어도 **환불 버튼 하나는 남긴다**(P3).
              탈퇴 가드는 신규 원매출 환불 라우트 한 곳에서만 풀렸다(G4). 액션 바를 통째로
              숨기면 그 유일한 경로에 화면에서 도달할 방법이 없어져 개방이 무의미해진다.
            */}
            <Row className=" self-end">
                {/* 삭제·상태 변경은 서버가 update/delete 를 소프트 삭제에 열지 않아 404 로 떨어진다.
                    누를 때마다 "오류 코드 404" 알럿만 뜨는 대신 이유를 말한다(MB M1과 동일). */}
                {!isReadOnly && (
                    <Col
                        className={`w-[140px] h-[71px] justify-center items-center rounded-[9px] mr-[9px] ${
                            isLifecycleEnded
                                ? 'bg-[#E5E7EB] cursor-not-allowed'
                                : 'bg-[#3D4244] cursor-pointer'
                        }`}
                        title={isLifecycleEnded ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                        onClick={() => {
                            if (isLifecycleEnded) {
                                showLifecycleEndedBlocked();
                                return;
                            }
                            if (isEmptySchedule) {
                                onClickDelete();
                            } else {
                                onDeleteError();
                            }
                        }}
                    >
                        <RegularText
                            fontSize={isLifecycleEnded ? 15 : 20}
                            lineHeight={24}
                            letterSpacing={-0.3}
                            color={isLifecycleEnded ? '#9CA3AF' : '#FFFFFF'}
                        >
                            {isLifecycleEnded ? '종료된 수강권' : '삭제'}
                        </RegularText>
                    </Col>
                )}
                {!isReadOnly && (
                    <Col
                        className={`w-[140px] h-[71px] justify-center items-center rounded-[9px] mr-[9px] ${
                            isLifecycleEnded
                                ? 'bg-[#E5E7EB] cursor-not-allowed'
                                : 'bg-[#ACACAC] cursor-pointer'
                        }`}
                        title={isLifecycleEnded ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                        onClick={() => {
                            if (isLifecycleEnded) {
                                showLifecycleEndedBlocked();
                                return;
                            }
                            if (isActive) {
                                // 수강권 만료
                                onClickExpire();
                            } else {
                                // 수강권 재활성화
                                onClickReactivation();
                            }
                        }}
                    >
                        <RegularText
                            fontSize={isLifecycleEnded ? 15 : 20}
                            lineHeight={24}
                            letterSpacing={-0.3}
                            color={isLifecycleEnded ? '#9CA3AF' : '#FFFFFF'}
                        >
                            {isLifecycleEnded ? '종료된 수강권' : isActive ? '만료' : '재활성화'}
                        </RegularText>
                    </Col>
                )}
                <Col
                    className="w-[140px] h-[71px] bg-[#FF20201A] justify-center items-center rounded-[9px] border border-solid border-[#FF20200D] mr-[9px] cursor-pointer"
                    onClick={onClickRefund}
                >
                    <RegularText fontSize={20} lineHeight={24} letterSpacing={-0.3} color="#FF2020">
                        환불
                    </RegularText>
                </Col>
                {!isReadOnly && (
                    <Col
                        className={`w-[140px] h-[71px] justify-center items-center rounded-[9px] border border-solid mr-[9px] ${
                            isUpgradeBlocked
                                ? 'bg-[#F3F4F6] border-[#E5E7EB] cursor-not-allowed'
                                : 'bg-[#9536D41A] border-[#9536D40D] cursor-pointer'
                        }`}
                        title={isUpgradeBlocked ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                        onClick={isUpgradeBlocked ? showLifecycleEndedBlocked : onClickUpgrade}
                    >
                        <RegularText
                            fontSize={isUpgradeBlocked ? 15 : 20}
                            lineHeight={24}
                            letterSpacing={-0.3}
                            color={isUpgradeBlocked ? '#9CA3AF' : '#9536D4'}
                        >
                            {isUpgradeBlocked ? '업그레이드 불가' : '업그레이드'}
                        </RegularText>
                    </Col>
                )}
                {!isReadOnly && onClickConvert && (
                    <Col
                        className="w-[140px] h-[71px] bg-[#2563EB1A] justify-center items-center rounded-[9px] border border-solid border-[#2563EB0D] mr-[9px] cursor-pointer"
                        onClick={onClickConvert}
                    >
                        <RegularText fontSize={20} lineHeight={24} letterSpacing={-0.3} color="#2563EB">
                            수강권 전환
                        </RegularText>
                        <RegularText fontSize={11} lineHeight={16} letterSpacing={-0.2} color="#2563EBB3">
                            그룹 · 회차 공유
                        </RegularText>
                    </Col>
                )}
                {/* 인계 여부와 무관하게 같은 기준으로 노출한다. 서버가 계보 각 세대의
                    미수금에 원판매부터 순서대로 배분하므로 어느 문서에도 초과 결제가 없다.

                    종료된 수강권만 예외다(P17). 전액 환불해도 원판매 totalPrice 는 보존되므로
                    미수금 산식이 환불액만큼 "유령 미수금"을 만들고, 그대로 결제하면 근거 없는
                    양수 매출행이 생겨 정산에 잡힌다. 서버가 409 로 막으므로 버튼도 잠근다. */}
                {!isReadOnly && (displayUnpaidPrice ?? 0) > 0 && (
                    <Col
                        className={`w-[140px] h-[71px] justify-center items-center rounded-[9px] border border-solid mr-[9px] ${
                            isSplitPaymentBlocked
                                ? 'bg-[#F3F4F6] border-[#E5E7EB] cursor-not-allowed'
                                : 'bg-[#15BA721A] border-[#15BA720D] cursor-pointer'
                        }`}
                        title={isSplitPaymentBlocked ? MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE : undefined}
                        onClick={isSplitPaymentBlocked ? showLifecycleEndedBlocked : onClickUnpaidPayment}
                    >
                        <RegularText
                            fontSize={isSplitPaymentBlocked ? 15 : 20}
                            lineHeight={24}
                            letterSpacing={-0.3}
                            color={isSplitPaymentBlocked ? '#9CA3AF' : '#15BA72'}
                        >
                            {isSplitPaymentBlocked ? '미수금 결제 불가' : '미수금 결제'}
                        </RegularText>
                    </Col>
                )}
            </Row>
        </Col>
    );
};

export default MemberMembershipManageComponents;

//   {/* 메모 */}
//   <Col className="mb-[40px] w-[786px] h-[106px] ">
//   <Body3Regular className="text-LAVEL_1">메모</Body3Regular>
//   <Body2Regular className="text-LAVEL_4 w-full break-all h-full line-clamp-3 text-ellipsis">
//       {membership.memo}
//   </Body2Regular>
// </Col>

// <Row className="w-full justify-between">
// {isActive ? (
//     isEmptySchedule ? (
//         <NewWhiteRoundButton
//             color={'GREY_300'}
//             text={'수강권 삭제'}
//             width={120}
//             height={24}
//             fontType={'CaptionRegular'}
//             onClick={() =>
//                 deleteMemberShipMutation.mutate({
//                     membershipId: membership._id,
//                     accessToken: authorization,
//                 })
//             }
//         />
//     ) : (
//         <NewWhiteRoundButton
//             color={'GREY_300'}
//             text={'수강권 만료'}
//             width={88}
//             height={24}
//             fontType={'CaptionRegular'}
//             onClick={() =>
//                 updateMemberShipMutation.mutate({
//                     membershipId: membership._id,
//                     status: 'expired',
//                     accessToken: authorization,
//                 })
//             }
//         />
//     )
// ) : (
//     <Row className=" items-center ">
//         <NewWhiteRoundButton
//             color={'primary'}
//             text={'수강권 재활성화'}
//             width={120}
//             height={24}
//             className="mr-[16px]"
//             fontType={'CaptionRegular'}
//             disabled={!isRemainSession}
//             onClick={() => {
//                 if (!isRemainSession) {
//                     createAlert(
//                         '회차 조절이 필요해요!',
//                         `만료된 수강권을 활성화 시키려면 ‘수강권 수정하기’를 눌러\n‘총 회차’를 늘려주세요!`,
//                         true
//                     );
//                 } else {
//                     updateMemberShipMutation.mutate({
//                         membershipId: membership._id,
//                         status: 'active',
//                         accessToken: authorization,
//                     });
//                 }
//             }}
//         />
//         {isRemainSession ? (
//             <CaptionRegular className="" style={{ color: colors.LAVEL_1 }}>
//                 회차가 남아있는 수강권은 재활성화가 가능해요!
//             </CaptionRegular>
//         ) : (
//             <>
//                 <CaptionRegular style={{ color: colors.LAVEL_1 }}>
//                     만료된 수강권을 활성화 시키시려면
//                 </CaptionRegular>
//                 <CaptionRegular className="" style={{ color: colors.LAVEL_1 }}>
//                     회차를 조절해주세요!
//                 </CaptionRegular>
//             </>
//         )}
//     </Row>
// )}
// <NewColorRoundButton
//     text={'수강권 수정'}
//     bgColor="primary"
//     textColor="white"
//     width={126}
//     height={36}
//     onClick={onClickUpdate}
// />
// </Row>
